<template>
  <div
    class="h-full space-y-4 flex flex-col justify-between bg-white overflow-hidden"
  >
    <agent-view
      open
      isEmbedded
      hideHeader
      autofocus
      ref="agentViewRef"
      hostApp="copilot"
      :defaultAgentId="defaultAgentId"
      :locale="locale"
      :username="username"
      :appContext="agentContext"
      :logContext="logContext"
      :loading="copilotLoading"
      :hideInput="shouldDisableInput"
      :scopeOptions="scopeOptions"
      :filtersOptions="agentFilters"
      :config="config"
      @go-to-content="emit('go-to-content', $event)"
      @action="emit('action', $event)"
    >
      <template #empty-state v-if="shouldDisplayCopilotEmptyState">
        <div
          class="flex flex-col items-center justify-start flex-1 w-full overflow-x-hidden overflow-y-auto space-y-6"
        >
          <div
            class="flex flex-col items-center space-y-6 min-h-56 flex-none justify-end"
          >
            <div class="text-xl text-mayday-ai-gradient">
              {{
                $t('genius.empty-state.title', {
                  username: firstname,
                })
              }}
            </div>
            <div class="text-sm text-body text-center">
              {{ $t('genius.empty-state.description') }}
            </div>
            <BaseButton
              size="md"
              :text="$t('genius.empty-state.cta')"
              @click="handleGenerateAnswer"
            />
          </div>
          <div
            v-if="manualAutomationsLoading || manualAutomations.length"
            class="flex flex-col items-center space-y-6 w-full"
          >
            <div class="flex items-center space-x-3 w-full">
              <div class="w-full border-t border-gray-2" />
              <div class="uppercase text-caption text-xs">
                {{ $t('genius.empty-state.or') }}
              </div>
              <div class="w-full border-t border-gray-2" />
            </div>
            <div class="flex flex-col items-start w-full space-y-2">
              <div class="text-xs text-caption">
                {{ $t('genius.empty-state.relevant-content') }}
              </div>
              <CopilotAutomations
                :lang="selectedLanguage"
                :manualAutomations="manualAutomations"
                :manualAutomationsLoading="manualAutomationsLoading"
                @go-to-content="emit('go-to-content', $event)"
              />
            </div>
          </div>
        </div>
      </template>
      <template #input-header>
        <div class="py-2 space-y-2">
          <BaseMessageBox
            v-if="testModeChanged"
            multiline
            variant="info"
            :title="$t('genius.ticket-warning.test-mode.title')"
            :message="$t('genius.ticket-warning.test-mode.description')"
            @close="testModeChanged = false"
          />
          <BaseMessageBox
            v-if="newTicket"
            multiline
            variant="warning"
            :title="$t('genius.ticket-warning.new-ticket.title')"
            :message="$t('genius.ticket-warning.new-ticket.description')"
            :actions="warningMessagesActions.new"
            :showClose="false"
          />
          <BaseMessageBox
            v-else-if="ticketLeft && !!currentAgentCopilotTool"
            multiline
            variant="warning"
            :title="$t('genius.ticket-warning.no-ticket.title')"
            :message="$t('genius.ticket-warning.no-ticket.description')"
            :actions="warningMessagesActions.empty"
            :showClose="false"
          />
          <BaseMessageBox
            v-else-if="ticketChanged && !!currentAgentCopilotTool"
            multiline
            variant="warning"
            :title="$t('genius.ticket-warning.change-ticket.title')"
            :message="$t('genius.ticket-warning.change-ticket.description')"
            :actions="warningMessagesActions.change"
            :showClose="false"
          />
        </div>
      </template>
    </agent-view>
  </div>
</template>

<script setup lang="ts">
import {
  useCopilotStore,
  useAdminStore,
  useKnowledgeStore,
  ExternalContent,
  Label,
} from '@getmayday/vue-modules';
import { useAgentStore } from '@getmayday/vue-modules/agent';
import CopilotAutomations from './CopilotAutomations.vue';
import type { LightEntity } from '@getmayday/vue-modules';

const copilotStore = useCopilotStore();
const {
  copilotLoading,
  manualAutomations,
  manualAutomationsLoading,
  focusedTicket,
  copilotErrored,
  copilotHasData,
} = storeToRefs(copilotStore);

const configStore = useConfigStore();
const { currentTabContext, locale, geniusTestMode } = storeToRefs(configStore);

const adminStore = useAdminStore();
const {
  username,
  hasCompanyBrainAgentsPreference,
  hasHideGeniusMicrophone,
  companyHasIntegration,
} = storeToRefs(adminStore);

const agentStore = useAgentStore();
const {
  currentSession,
  currentAgent,
  availableAgents,
  currentAgentCopilotTool,
  historyConversationId,
  getAgentsWithTool,
} = storeToRefs(agentStore);

const knowledgeStore = useKnowledgeStore();
const { selectedLanguage, path, content, focusKnowledge, modalContent } =
  storeToRefs(knowledgeStore);

const { defaultAgentId } = useCopilot();

const router = useRouter();

const { t } = useI18n();

const emit = defineEmits<{
  (event: 'action', payload: any): void;
  (event: 'go-to-content', payload: any): void;
  (event: 'get-recommendations'): void;
}>();

const ticketLeft = ref(false);
const newTicket = ref(false);
const ticketChanged = ref(false);
const testModeChanged = ref(false);

const agentViewRef = ref(null as any | null);
const labels: Ref<Label[]> = ref([]);

onMounted(async () => {
  labels.value = await knowledgeStore.getAllContentLabels();
});

const warningMessagesActions = computed(() => ({
  change: [
    {
      text: t('genius.ticket-warning.change-ticket.ignore'),
      variant: 'tertiary',
      onClick: () => {
        ticketChanged.value = false;
      },
    },
    {
      text: t('genius.ticket-warning.change-ticket.analyse'),
      variant: 'secondary',
      onClick: handleAnalyseNewTicket,
    },
  ],
  empty: [
    {
      text: t('genius.ticket-warning.no-ticket.confirm'),
      variant: 'tertiary',
      onClick: () => {
        ticketLeft.value = false;
      },
    },
    {
      text: t('genius.ticket-warning.no-ticket.new-conversation'),
      variant: 'secondary',
      onClick: handleNewConversation,
    },
  ],
  new: [
    {
      text: t('genius.ticket-warning.new-ticket.ignore'),
      variant: 'tertiary',
      onClick: () => {
        newTicket.value = false;
      },
    },
    {
      text: t('genius.ticket-warning.new-ticket.analyse'),
      variant: 'secondary',
      onClick: handleAnalyseNewTicket,
    },
  ],
}));

const config = computed(() => ({
  hasHideGeniusMicrophone: hasHideGeniusMicrophone.value,
}));

const hasUserAccessToCopilotAgent = computed(() => {
  return getAgentsWithTool.value('copilot')?.length;
});

const isTicketFocused = computed(
  () => !!currentTabContext.value?.conversationId,
);

const hasConversationStarted = computed(
  () => !!currentSession.value?.conversations?.length,
);

const agentFilters = computed(() => {
  return {
    labels: labels.value,
  };
});

const shouldDisplayError = computed(() => {
  return (
    !copilotLoading.value &&
    copilotErrored.value &&
    !copilotHasData.value &&
    isTicketFocused.value &&
    !!currentAgentCopilotTool.value
  );
});

const shouldDisableInput = computed(() => {
  return (
    !hasCompanyBrainAgentsPreference.value ||
    shouldDisplayError.value ||
    !availableAgents.value?.length
  );
});

const shouldDisplayCopilotEmptyState = computed(() => {
  return (
    !hasConversationStarted.value &&
    currentTabContext.value?.tabSource &&
    isTicketFocused.value &&
    !!getAgentsWithTool.value('copilot_v2')?.length &&
    companyHasIntegration.value(currentTabContext.value?.tabSource)
  );
});

const copilotToolParameters = computed(() => {
  const toolArguments = currentAgentCopilotTool.value?.arguments;
  if (!toolArguments)
    return {
      displayMoodAnalysis: true,
      displayManualRecommendations: true,
      displayBrainRecommendations: true,
      displayTicketSummary: true,
      displayGenerateAnswerCta: true,
    };
  const {
    display_mood_analysis,
    display_manual_recommendations,
    display_brain_recommendations,
    display_ticket_summary,
    display_generate_answer_cta,
  } = toolArguments;

  return {
    displayMoodAnalysis:
      display_mood_analysis?.isChecked !== undefined
        ? display_mood_analysis.isChecked
        : true,
    displayManualRecommendations:
      display_manual_recommendations?.isChecked !== undefined
        ? display_manual_recommendations.isChecked
        : true,
    displayBrainRecommendations:
      display_brain_recommendations?.isChecked !== undefined
        ? display_brain_recommendations.isChecked
        : true,
    displayTicketSummary:
      display_ticket_summary?.isChecked !== undefined
        ? display_ticket_summary.isChecked
        : true,
    displayGenerateAnswerCta:
      display_generate_answer_cta?.isChecked !== undefined
        ? display_generate_answer_cta.isChecked
        : true,
  };
});

const shouldHideCopilot = computed(() => {
  return Object.keys(copilotToolParameters.value).every(
    (key) =>
      copilotToolParameters.value![
        key as keyof typeof copilotToolParameters.value
      ] === false,
  );
});

const agentContext = computed(() => {
  return {
    knowledgeId: focusKnowledge.value?.id,
    language: selectedLanguage.value,
    ticketId: focusedTicket.value || currentTabContext.value?.conversationId,
    helpdesk: currentTabContext.value?.tabSource,
    ticketFocused: isTicketFocused.value,
    copilotEmptyState: shouldDisplayCopilotEmptyState.value,
    automations: manualAutomations.value.map(
      (automation: LightEntity | ExternalContent) => ({
        entityId: automation.id,
        entityType: automation.hasOwnProperty('type')
          ? 'attached-contents'
          : 'external-contents',
      }),
    ),
    ...(isTicketFocused.value
      ? {}
      : {
          disabledAgents: {
            'mayday:copilot': t('genius.disabled-agent.copilot'),
          },
        }),
    currentPath: path.value.reverse(),
  };
});

const logContext = computed(() => {
  return {
    trigger: 'tab',
    platform: 'zendesk',
  };
});

const handleFetchRecommendations = async () => {
  newTicket.value = false;
  ticketLeft.value = false;
  ticketChanged.value = false;
  emit('get-recommendations');
};

const handleAnalyseNewTicket = async () => {
  await handleNewConversation();
  handleGenerateAnswer();
};

const setCopilotV2Agent = () => {
  const eligibleAgents = getAgentsWithTool.value('copilot_v2');
  if (!eligibleAgents?.length) return;
  agentStore.setCurrentAgent(eligibleAgents[0].id);
};

const handleGenerateAnswer = (shouldLog = true) => {
  if (!agentViewRef) return;
  if (!currentAgentCopilotTool.value) {
    setCopilotV2Agent();
    if (!currentAgentCopilotTool.value) return;
  }
  if (shouldLog && currentTabContext.value?.conversationId) {
    copilotStore.logTriggerTicketAnalysis(
      currentTabContext.value.conversationId,
      currentTabContext.value.tabSource,
      'agent',
    );
  }
  nextTick(() =>
    agentViewRef.value.sendMessage(t('genius.empty-state.analyze-ticket')),
  );
};

const handleNewConversation = async () => {
  if (!agentViewRef.value) return;
  ticketLeft.value = false;
  newTicket.value = false;
  ticketChanged.value = false;
  copilotStore.reset();
  if (
    !currentTabContext?.value?.conversationId &&
    currentAgent.value?.id === 'mayday:copilot'
  )
    agentStore.setCurrentAgent(null);
  await agentViewRef.value.newSession();
};

const toggleSessionsPanel = async () => {
  if (!agentViewRef.value) return;
  await agentViewRef.value.toggleSessionsPanel();
};

const goToHome = () => {
  router.push({ path: '/' });
};

watch(
  () => currentTabContext.value?.conversationId,
  (newVal, oldVal) => {
    ticketChanged.value = false;
    newTicket.value = false;
    ticketLeft.value = false;
    // Check if ticket is left
    if (
      !newVal &&
      !!oldVal &&
      !!currentAgentCopilotTool.value &&
      copilotHasData.value
    ) {
      return (ticketLeft.value = true);
    }
    // Check if new ticket
    if (
      newVal &&
      (!oldVal || !!!currentAgentCopilotTool.value) &&
      hasConversationStarted.value &&
      hasUserAccessToCopilotAgent.value
    ) {
      return (newTicket.value = true);
    }
    // Check if ticket changed
    if (
      newVal &&
      oldVal &&
      newVal !== focusedTicket.value &&
      (copilotHasData.value || copilotLoading.value)
    ) {
      return (ticketChanged.value = true);
    }
  },
);

watch(
  () => copilotLoading.value,
  () => {
    copilotStore.updateHasUserOpenedCopilot(true);
  },
  { immediate: true },
);

watch(
  () => geniusTestMode.value,
  (newVal, oldVal) => {
    testModeChanged.value = !!newVal && newVal !== oldVal;
  },
);

watch(
  () => currentAgentCopilotTool.value,
  (newVal) => {
    if (!newVal || historyConversationId.value) {
      return copilotStore.reset();
    }
    handleFetchRecommendations();
  },
);

watch(historyConversationId, (newVal) => {
  if (!newVal) return;
  copilotStore.reset();
});

// Scope Select
interface scopeOption {
  id: string;
  key: string;
  label: string;
}
const scopeOptions = ref<{
  Knowledge: scopeOption | null;
  Case: scopeOption | null;
  Content: scopeOption | null;
}>({ Knowledge: null, Case: null, Content: null });

const findEntity = (key: string, path: LightEntity[]) => {
  const entity = path.find((entity) => entity.type === key);
  if (!entity) return null;
  return { id: entity.id, key, label: entity.label };
};

const optKnowledge = computed(() => {
  if (!focusKnowledge.value) return null;
  const { id, label } = focusKnowledge.value;
  return { id, key: 'Knowledge', label };
});

const optCase = computed(() => findEntity('Case', [...path.value].reverse()));

const optContent = computed(() => {
  const focus =
    (content.value.type && content.value.type !== 'Case' && content) ||
    (modalContent.value.type &&
      modalContent.value.type !== 'Answer' &&
      modalContent);

  if (!focus) return null;

  const { id, label } = focus.value;
  return { id, key: 'Content', label };
});

onBeforeMount(() => {
  scopeOptions.value = {
    Knowledge: optKnowledge.value,
    Case: optCase.value,
    Content: optContent.value,
  };
});

onMounted(() => {
  const currentRouteParams = router.currentRoute.value.query;
  const shouldTriggerCopilot = currentRouteParams.trigger === 'true';

  if (shouldTriggerCopilot && !copilotLoading.value) {
    router.replace({ query: { ...currentRouteParams, trigger: 'false' } });
    nextTick(() => {
      handleGenerateAnswer(false);
    });
  }
});

defineExpose({
  handleNewConversation,
  toggleSessionsPanel,
});
</script>

<style scoped></style>
